/**
 * How far the on-screen keyboard overlaps the bottom of the layout viewport, 0 when it is closed.
 * iOS never shrinks the layout viewport for the keyboard, only the visual viewport inside it.
 */
export function keyboardInset(viewport: VisualViewport): number {
  return Math.max(0, Math.round(window.innerHeight - viewport.height - viewport.offsetTop));
}

/**
 * Height of the on-screen keyboard, 0 when it is closed. Unlike `keyboardInset`, panning the visual
 * viewport does not change it.
 */
export function keyboardHeight(viewport: VisualViewport): number {
  return Math.max(0, Math.round(window.innerHeight - viewport.height));
}

/**
 * Scrolls the least distance that brings `element` inside the visual viewport, the part of the page
 * left visible above the keyboard. `scrollIntoView` measures against the layout viewport, which
 * extends behind the keyboard, so it counts a field hidden by the keyboard as already in view.
 *
 * scroll-margin-top is an inset from the top of the layout viewport and stops applying once the
 * visual viewport is panned below it; scroll-margin-bottom is an inset from the bottom of the visual
 * viewport. An element taller than the visible band aligns its bottom edge.
 */
export function scrollAboveKeyboard(element: HTMLElement | null) {
  const viewport = window.visualViewport;
  if (!element || !viewport) return;

  const style = getComputedStyle(element);
  const margin = (side: "top" | "bottom") => parseFloat(style.getPropertyValue(`scroll-margin-${side}`)) || 0;
  const rect = element.getBoundingClientRect();
  const visibleTop = Math.max(viewport.offsetTop, margin("top"));
  const visibleBottom = viewport.offsetTop + viewport.height - margin("bottom");

  const overflowBelow = rect.bottom - visibleBottom;
  const overflowAbove = visibleTop - rect.top;
  const distance = overflowBelow > 0 ? overflowBelow : overflowAbove > 0 ? -overflowAbove : 0;
  if (distance === 0) return;

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  window.scrollBy({ top: distance, behavior: reduceMotion ? "auto" : "smooth" });
}
